import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const emptyForm = {
  room_number: '',
  room_type_id: '',
  floor_no: '',
  block_name: '',
  housekeeping_status: 'CLEAN',
  occupancy_status: 'VACANT',
  maintenance_status: 'NONE',
  is_active: true
};

function PmsRoomMaster({ token, selectedProperty, onNavigateToTab }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadData = async () => {
    if (!selectedProperty?.id) {
      setRows([]);
      setTypes([]);
      return;
    }
    try {
      const [typesRes, roomsRes] = await Promise.all([
        fetch('/api/v1/pms/room-types?all=1', { headers: headers() }),
        fetch('/api/v1/pms/rooms?all=1', { headers: headers() })
      ]);
      const tData = await typesRes.json();
      const rData = await roomsRes.json();
      if (!typesRes.ok) throw new Error(tData?.error || 'Failed to load room types');
      if (!roomsRes.ok) throw new Error(rData?.error || 'Failed to load rooms');
      setTypes(Array.isArray(tData) ? tData.filter((t) => t.is_active !== false) : []);
      setRows(Array.isArray(rData) ? rData : []);
    } catch (err) {
      setError(err?.message || 'Failed to load room master');
    }
  };

  useEffect(() => {
    setError('');
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const save = async (forceRetype = false) => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!editingId) return setError('Room creation is disabled here. Use Room Config tab to create rooms.');
    if (!String(form.room_number || '').trim()) return setError('Room number is required');
    if (!form.room_type_id) return setError('Room type is required');
    setError(''); setMessage('');
    try {
      const payload = { ...form, room_type_id: Number(form.room_type_id), force_retype: forceRetype };
      const res = await fetch(`/api/v1/pms/rooms/${editingId}`, { method: 'PUT', headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (res.status === 409 && data?.code === 'ROOM_TYPE_CHANGE_BLOCKED') {
        const list = (data.conflicts || []).map((c) =>
          `  • ${c.reservation_no} (${c.status}) — ${c.guest_name || '-'}, ${c.arrival_date} → ${c.departure_date}`
        ).join('\n');
        const msg =
          `Room ${form.room_number} has ${data.conflicts?.length || 0} active/future reservation(s):\n\n${list}\n\n` +
          `Changing the room type will leave these bookings attached to a room whose type no longer matches what was sold. ` +
          `This may cause tariff mismatch on existing folios.\n\n` +
          `Recommended: cancel or reassign those reservations first.\n\nContinue anyway?`;
        if (window.confirm(msg)) {
          await save(true);
        }
        return;
      }
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage(`Room ${form.room_number} updated successfully`);
      setForm(emptyForm);
      setEditingId('');
      loadData();
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this room?')) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/pms/rooms/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      loadData();
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const editRow = (row) => {
    setEditingId(String(row.room_id));
    setForm({
      room_number: row.room_number || '',
      room_type_id: String(row.room_type_id || ''),
      floor_no: row.floor_no || '',
      block_name: row.block_name || '',
      housekeeping_status: row.housekeeping_status || 'CLEAN',
      occupancy_status: row.occupancy_status || 'VACANT',
      maintenance_status: row.maintenance_status || 'NONE',
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  const formDisabled = !editingId;

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Room Master</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div style={{ marginBottom: '0.6rem', fontSize: '0.9rem', color: '#334155' }}>
        This page is for editing existing rooms only. To create a new room, please navigate to{' '}
        {typeof onNavigateToTab === 'function' ? (
          <button
            type="button"
            onClick={() => onNavigateToTab('room-inventory')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--theme-primary)',
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >Room Config</button>
        ) : (
          <b>Room Config</b>
        )}.
      </div>

      {!editingId ? (
        <div style={{ marginBottom: '0.6rem', padding: '0.55rem 0.85rem', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '7px', fontSize: '0.85rem', color: '#1e40af' }}>
          Click <b>Edit</b> on a room in the list below to modify it.
        </div>
      ) : (
        <div style={{ marginBottom: '0.6rem', padding: '0.45rem 0.85rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '7px', fontSize: '0.85rem', color: '#15803d' }}>
          Editing Room <b>{form.room_number}</b> — make changes and click <b>Update Room</b>.
        </div>
      )}

      <div ref={formRef} className="filter-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '0.6rem', alignItems: 'start', opacity: formDisabled ? 0.55 : 1, pointerEvents: formDisabled ? 'none' : 'auto', ...editHighlightStyle(editHighlight) }}>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Number <span style={{ color: '#b91c1c' }}>*</span></span>
          <input
            placeholder="—"
            value={form.room_number}
            readOnly={formDisabled}
            onChange={(e) => setForm({ ...form, room_number: e.target.value })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Must be unique per hotel.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Type <span style={{ color: '#b91c1c' }}>*</span></span>
          <select value={form.room_type_id} onChange={(e) => setForm({ ...form, room_type_id: e.target.value })} disabled={formDisabled}>
            <option value="">Select Room Type</option>
            {types.map((t) => <option key={t.room_type_id} value={t.room_type_id}>{t.room_type_name}</option>)}
          </select>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Deluxe, Suite, etc.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Floor</span>
          <input
            placeholder="Ex: 1 / Ground"
            value={form.floor_no}
            readOnly={formDisabled}
            onChange={(e) => setForm({ ...form, floor_no: e.target.value })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>For filter and room view.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Block/Wing</span>
          <input
            placeholder="Ex: A / East Wing"
            value={form.block_name}
            readOnly={formDisabled}
            onChange={(e) => setForm({ ...form, block_name: e.target.value })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>For operational filters.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Housekeeping Status</span>
          <select value={form.housekeeping_status} onChange={(e) => setForm({ ...form, housekeeping_status: e.target.value })} disabled={formDisabled}>
            <option value="CLEAN">CLEAN</option>
            <option value="DIRTY">DIRTY</option>
          </select>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Cleaning state.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Occupancy Status</span>
          <select value={form.occupancy_status} onChange={(e) => setForm({ ...form, occupancy_status: e.target.value })} disabled={formDisabled}>
            <option value="VACANT">VACANT</option>
            <option value="OCCUPIED">OCCUPIED</option>
            <option value="RESERVED">RESERVED</option>
          </select>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Guest occupancy state.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Maintenance Status</span>
          <select value={form.maintenance_status} onChange={(e) => setForm({ ...form, maintenance_status: e.target.value })} disabled={formDisabled}>
            <option value="NONE">NONE</option>
            <option value="OUT_OF_ORDER">OUT_OF_ORDER</option>
            <option value="PAINTING">PAINTING</option>
            <option value="BLOCKED">BLOCKED</option>
          </select>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Engineering/FO block.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Record Active Flag</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <input type="checkbox" checked={form.is_active} disabled={formDisabled} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </span>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Uncheck to hide from ops.</span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', paddingTop: '1.4rem' }}>
          <button onClick={() => save(false)} disabled={formDisabled} style={{ padding: '0.45rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', cursor: formDisabled ? 'not-allowed' : 'pointer', width: 'auto' }}>Update Room</button>
          <button onClick={() => { setEditingId(''); setForm(emptyForm); setError(''); setMessage(''); }} style={{ padding: '0.45rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', width: 'auto' }}>Clear</button>
        </div>
      </div>

      <div style={{ marginTop: '0.8rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Room</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Type</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Floor/Block</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.room_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ padding: '0.45rem' }}>{r.room_number}</td>
                <td style={{ padding: '0.45rem' }}>{r.room_type_name}</td>
                <td style={{ padding: '0.45rem' }}>{[r.floor_no, r.block_name].filter(Boolean).join(' / ') || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.occupancy_status} / {r.housekeeping_status} / {r.maintenance_status}</td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem' }}>
                  <button onClick={() => editRow(r)}>Edit</button>
                  {r.is_active ? <button onClick={() => remove(r.room_id)}>Deactivate</button> : null}
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td style={{ padding: '0.6rem' }} colSpan={5}>No rooms found</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsRoomMaster;
